/**
 * DL Fork: useDlMobile — host-signaled mobile detection for the embedded
 * build.
 *
 * Detection model
 * ---------------
 * The host application appends `?dl_mobile=1` to the iframe URL when it
 * detects a phone-sized viewport (`window.matchMedia('(max-width: 767px)').matches`).
 * This fork captures that value EXACTLY ONCE, at module-import time, and
 * treats it as immutable for the iframe's lifetime.
 *
 * Why module-load-once and not a viewport listener:
 * - Lightdash's router rewrites the URL during initial render (e.g.,
 *   `<Navigate>` to `/projects/:uuid`), stripping `?dl_mobile=1` before any
 *   `useEffect` fires. Same problem `parentMessaging.ts` solves for
 *   `targetOrigin`.
 * - Capturing once is cheap, deterministic, and immune to background-tab
 *   reload / re-render / HMR. CRITICAL: the previous `MobileRoutes.tsx`
 *   implementation read `window.innerWidth` at render time and was disabled
 *   in `App.tsx` because backgrounded tabs could flip non-mobile users
 *   into mobile mode on reload. Re-creating that bug class is explicitly
 *   forbidden.
 *
 * Triple-gate
 * -----------
 * Mobile UX activates only when BOTH:
 *   IS_EMBEDDED_BUILD === true   (ruled out for standalone deployments)
 *   AND  ?dl_mobile=1             (host-app signal)
 * The standalone build is never loaded inside the host iframe, so an attacker
 * passing `?dl_mobile=1` to a standalone deployment cannot trigger the mode.
 *
 * Test ergonomics
 * ---------------
 * `__setDlMobileForTest(value)` lets vitest tests override the captured
 * value without having to re-import the module after mutating
 * `window.location.search`. Production code paths must NOT call this.
 */
import { IS_EMBEDDED_BUILD } from '../ee/features/embed/parentMessaging';

const INITIAL_DL_MOBILE: boolean = (() => {
    try {
        if (typeof window === 'undefined') return false;
        return (
            new URLSearchParams(window.location.search).get('dl_mobile') === '1'
        );
    } catch {
        return false;
    }
})();

let testOverride: boolean | null = null;

export function useDlMobile(): boolean {
    if (testOverride !== null) return testOverride;
    return INITIAL_DL_MOBILE;
}

/**
 * Combined helper: triple-gates the wrapper signal on the embedded build
 * flag. Use this everywhere except where the call site already gates on
 * `IS_EMBEDDED_BUILD` for unrelated reasons.
 *
 * `useDlMobile` is called unconditionally (it has no internal state and
 * simply reads the module-load capture / test override) so the eslint
 * `react-hooks/rules-of-hooks` rule is satisfied — we then AND the result
 * with the build-time constant.
 */
export function useIsDlMobile(): boolean {
    const isMobileSignal = useDlMobile();
    return IS_EMBEDDED_BUILD && isMobileSignal;
}

/**
 * TEST ONLY: override the captured `dl_mobile` value. Pass `null` to clear
 * the override and fall back to the real module-load capture. Not exported
 * via a barrel — vitest must import this file directly.
 *
 * Do NOT call from production code paths.
 */
export function __setDlMobileForTest(value: boolean | null): void {
    testOverride = value;
}
